/**
 * Haptic feedback — works on iOS / Android via Capacitor, and gracefully
 * degrades to the Web Vibration API (or no-op) on the web.
 *
 * Usage:
 *   import { haptic } from "@/lib/haptics";
 *   haptic.tap();        // light bump — e.g. set logged
 *   haptic.medium();     // medium bump — set/rest boundary
 *   haptic.heavy();      // heavy bump — final rep
 *   haptic.success();    // double-tap "done" — workout complete, PR set
 *   haptic.warning();    // attention — about to fail
 *   haptic.error();      // failure pattern — couldn't log
 *
 * All methods are fire-and-forget (no awaits in callers needed). The dynamic
 * import keeps the native plugin out of the web bundle's critical path.
 */
import { isNativeApp } from "./platform";

const fallbackVibrate = (pattern) => {
  if (typeof navigator === "undefined" || !navigator.vibrate) return;
  try {
    navigator.vibrate(pattern);
  } catch (err) {
    // navigator.vibrate is best-effort — Safari and some Android browsers
    // throw a SecurityError without a recent user gesture. Non-fatal.
    console.debug("navigator.vibrate failed", err);
  }
};

const runImpact = async (style, webMs) => {
  if (isNativeApp()) {
    try {
      const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
      await Haptics.impact({ style: ImpactStyle[style] });
      return;
    } catch (e) {
      // Plugin not registered — fall through to web fallback.
      console.warn("haptics impact failed:", e?.message);
    }
  }
  fallbackVibrate(webMs);
};

const runNotification = async (type, webPattern) => {
  if (isNativeApp()) {
    try {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({ type: NotificationType[type] });
      return;
    } catch (e) {
      console.warn("haptics notification failed:", e?.message);
    }
  }
  fallbackVibrate(webPattern);
};

export const haptic = {
  tap:     () => runImpact("Light", 10),
  medium:  () => runImpact("Medium", 25),
  heavy:   () => runImpact("Heavy", 40),
  success: () => runNotification("Success", [25, 30, 25]),
  warning: () => runNotification("Warning", [40, 60, 40]),
  error:   () => runNotification("Error",   [80, 80, 80, 80, 80]),
};
